import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  ButtonLink,
  Card,
  Heading,
  Inline,
  Spinner,
  Stack,
  Text,
} from '../design/index.js';
import {
  disconnectWithings,
  fetchWithingsStatus,
  syncWithingsNow,
  withingsConnectUrl,
} from '../api.js';
import type { WithingsStatus } from '../api.js';
import styles from './WithingsCard.module.css';

/**
 * Throttles the on-open catch-up sync so remounting the dashboard (StrictMode,
 * a tab regaining focus, a re-render) does not hammer Withings. `sessionStorage`
 * is wrapped in try/catch — it can throw in private browsing.
 */
const THROTTLE_MS = 5 * 60 * 1000;
const THROTTLE_KEY = 'withings:lastAutoSyncAttempt';

function readLastAttempt(): number {
  try {
    const raw = window.sessionStorage.getItem(THROTTLE_KEY);
    return raw ? Number(raw) : 0;
  } catch {
    return 0;
  }
}

function writeLastAttempt(at: number): void {
  try {
    window.sessionStorage.setItem(THROTTLE_KEY, String(at));
  } catch {
    // A missed throttle record just costs one extra sync call — harmless.
  }
}

/** "4 minutes ago" — a person's own words, not a raw timestamp, so a missed
 *  push reads as stale rather than looking identical to "nothing to sync". */
function formatSyncedAt(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'a little while ago';

  const diffSeconds = Math.round((Date.now() - then) / 1000);
  if (diffSeconds < 45) return 'moments ago';

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  for (const [unit, secondsInUnit] of units) {
    if (diffSeconds >= secondsInUnit || unit === 'minute') {
      return rtf.format(-Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return 'moments ago';
}

type CardState =
  | { kind: 'loading' }
  | { kind: 'status-error'; message: string }
  | { kind: 'disconnected' }
  | {
      kind: 'connected';
      status: WithingsStatus;
      syncing: boolean;
      syncError: string | null;
      confirmingDisconnect: boolean;
      disconnecting: boolean;
      disconnectError: string | null;
    };

/**
 * The dashboard's Withings section.
 *
 * Withings pushes; this app never polls it on a timer (see CLAUDE.md). The
 * only automatic call here is a single throttled catch-up on mount, plus
 * whatever the person triggers with "Sync now".
 */
export function WithingsCard() {
  const [state, setState] = useState<CardState>({ kind: 'loading' });
  const attemptedAutoSync = useRef(false);

  const loadStatus = useCallback(async () => {
    try {
      const status = await fetchWithingsStatus();
      setState(
        status.connected
          ? {
              kind: 'connected',
              status,
              syncing: false,
              syncError: null,
              confirmingDisconnect: false,
              disconnecting: false,
              disconnectError: null,
            }
          : { kind: 'disconnected' },
      );
    } catch (err) {
      setState({
        kind: 'status-error',
        message: err instanceof Error ? err.message : 'Could not reach the server.',
      });
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  /**
   * Set for a moment after a sync lands, so the card can confirm rather than
   * merely stop spinning.
   *
   * Without it, success and "nothing happened" look identical: the spinner
   * disappears and the timestamp changes to a phrase most people do not read
   * closely. A tick that draws itself is the difference between the app
   * answering and the app going quiet.
   */
  const [justSynced, setJustSynced] = useState(false);

  useEffect(() => {
    if (!justSynced) return;
    const timer = window.setTimeout(() => setJustSynced(false), 2400);
    return () => window.clearTimeout(timer);
  }, [justSynced]);

  const runSync = useCallback((markAttempt: boolean) => {
    if (markAttempt) writeLastAttempt(Date.now());
    setState((prev) => (prev.kind === 'connected' ? { ...prev, syncing: true, syncError: null } : prev));
    syncWithingsNow()
      .then((result) => {
        setState((prev) =>
          prev.kind === 'connected'
            ? { ...prev, syncing: false, syncError: null, status: { ...prev.status, lastSyncedAt: result.syncedAt } }
            : prev,
        );
        setJustSynced(true);
      })
      .catch((err) => {
        setState((prev) =>
          prev.kind === 'connected'
            ? { ...prev, syncing: false, syncError: err instanceof Error ? err.message : 'Withings sync failed.' }
            : prev,
        );
      });
  }, []);

  // Catch-up on page load: fires once per mount, only when a connection
  // exists, and only if the last attempt was more than five minutes ago. It
  // never blocks first render — the card above already shows what is known.
  useEffect(() => {
    if (state.kind !== 'connected' || attemptedAutoSync.current) return;
    attemptedAutoSync.current = true;
    if (Date.now() - readLastAttempt() < THROTTLE_MS) return;
    runSync(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  const handleSyncNow = useCallback(() => runSync(true), [runSync]);

  const handleDisconnect = useCallback(() => {
    setState((prev) => (prev.kind === 'connected' ? { ...prev, disconnecting: true, disconnectError: null } : prev));
    disconnectWithings()
      .then(() => setState({ kind: 'disconnected' }))
      .catch((err) => {
        setState((prev) =>
          prev.kind === 'connected'
            ? {
                ...prev,
                disconnecting: false,
                disconnectError: err instanceof Error ? err.message : 'Could not disconnect Withings.',
              }
            : prev,
        );
      });
  }, []);

  if (state.kind === 'loading') {
    return (
      <Card className={styles.card} data-testid="withings-loading">
        <Inline gap={3} align="center">
          <Spinner size="sm" aria-label="Checking your Withings connection" />
          <Text size="sm" color="muted">Checking your Withings connection…</Text>
        </Inline>
      </Card>
    );
  }

  if (state.kind === 'status-error') {
    return (
      <Card className={styles.card} data-testid="withings-status-error">
        <Alert variant="error" title="Couldn't check Withings">
          {state.message}
        </Alert>
      </Card>
    );
  }

  if (state.kind === 'disconnected') {
    return (
      <Card className={styles.card} data-testid="withings-disconnected">
        <Stack gap={4}>
          <Text size="xs" color="muted" className={styles.label}>Withings</Text>
          <Stack gap={2}>
            <Heading level={3} size="lg">Bring your scale and watch here</Heading>
            <Text size="sm" color="secondary">
              Connect your Withings account once, and your weight and heart rate
              arrive here on their own — nothing to enter by hand.
            </Text>
          </Stack>
          <ButtonLink href={withingsConnectUrl} className={styles.connect} data-testid="withings-connect-link">
            Connect Withings
          </ButtonLink>
        </Stack>
      </Card>
    );
  }

  const { status, syncing, syncError, confirmingDisconnect, disconnecting, disconnectError } = state;

  return (
    <Card className={styles.card} data-testid="withings-connected">
      <Stack gap={4}>
        <Inline justify="space-between" align="center">
          <Text size="xs" color="muted" className={styles.label}>Withings</Text>
          <Badge variant="positive" dot>Connected</Badge>
        </Inline>

        <Inline gap={3} align="center" wrap>
          <Text size="sm" data-testid="withings-last-synced">
            {status.lastSyncedAt ? `Synced ${formatSyncedAt(status.lastSyncedAt)}` : 'Not synced yet'}
          </Text>
          {syncing && <Spinner size="sm" aria-label="Syncing with Withings" />}
          {!syncing && justSynced && (
            /* The tick is DRAWN, not faded in — the same stroke-dashoffset
               technique Checkbox uses, from transitions.dev's success-check,
               on this product's own duration and easing. aria-hidden because
               the timestamp beside it already says what happened, in words
               that stay on the page after this has gone. */
            <span className={styles.syncedTick} aria-hidden="true" data-testid="withings-synced-tick">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 8.5l3 3 6-6" />
              </svg>
            </span>
          )}
        </Inline>

        {syncError && (
          <Alert variant="error" title="Sync failed" data-testid="withings-sync-error">
            {syncError} Nothing already saved was affected — try again in a moment.
          </Alert>
        )}

        {disconnectError && (
          <Alert variant="error" title="Couldn't disconnect" data-testid="withings-disconnect-error">
            {disconnectError}
          </Alert>
        )}

        <Inline gap={3} wrap>
          <Button variant="secondary" size="sm" onClick={handleSyncNow} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>

          {!confirmingDisconnect ? (
            <Button
              variant="ghost"
              size="sm"
              data-testid="withings-disconnect-button"
              onClick={() =>
                setState((prev) => (prev.kind === 'connected' ? { ...prev, confirmingDisconnect: true } : prev))
              }
            >
              Disconnect
            </Button>
          ) : (
            <Inline gap={3} wrap data-testid="withings-disconnect-confirm">
              <Text size="sm" color="secondary">
                Disconnect Withings? Your saved measurements stay right where they are.
              </Text>
              <Button variant="danger" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
                {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={disconnecting}
                onClick={() =>
                  setState((prev) => (prev.kind === 'connected' ? { ...prev, confirmingDisconnect: false } : prev))
                }
              >
                Cancel
              </Button>
            </Inline>
          )}
        </Inline>
      </Stack>
    </Card>
  );
}
